/**
 * The Semantic check: one step, always true or false, no judge call. The
 * step is true at the confidence the person picks. Choosing among options or
 * scoring on a scale is a judge's job (an LLM judge step), not this one's.
 *
 *   grounded   Is every claim of the answer in the context?   (hallucination)
 *              Claims with figures are checked by the figures.
 *   intent     What to measure, in the person's words, over a text they
 *              pick. How it is read depends on what it says (the server
 *              decides, and the editor shows it):
 *                about the text   "This message expresses frustration."
 *                answered         "Was the question answered?"   needs the
 *                                 question too: Input and output, the
 *                                 transcript, or the last reply
 *                repeated         "Did the user ask again?"   needs a
 *                                 conversation's transcript
 *   resembles  Is the text close to one of these examples?  ("Matches")
 *
 * Grounded and Statement run on the sandbox's small models (instrument
 * `cross_encoder`); Matches compares stored embeddings (`bi_encoder`, op
 * similarity). A node in an older shape — drift, route, relevance, numeric
 * outputs — reads as no check (`checkOf` → null) and keeps running as saved;
 * an older Says node ({pair: [text, {text}]}) reads as an Intent and is
 * rewritten to that shape on its next edit.
 */

export const CHECKS = [
  { id: 'grounded', label: 'Grounded', question: 'Is every claim in the answer supported by the context?' },
  { id: 'intent', label: 'Intent', question: 'Does what you describe hold? Answered, repeated, frustrated…' },
  { id: 'resembles', label: 'Matches', question: 'Is the text close to one of these examples?' },
];

export const DEFAULT_THRESHOLD = { grounded: 0.9, intent: 0.5, resembles: 0.85 };

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const LEVELS = ['run', 'span', 'conversation'];
const levelOf = (target) => (LEVELS.includes(target) ? target : 'run');
const boolField = (node) => {
  const entries = Object.entries(node?.output || {});
  return entries.length === 1 && entries[0][1]?.type === 'boolean' ? entries[0][0] : null;
};

/** Which check a node is, or null for a shape the editor does not express. */
export function checkOf(node) {
  if (!node || !boolField(node)) return null;
  const c = node.config || {};
  if (node.instrument === 'bi_encoder') return c.op === 'similarity' && isObj(c.against) && Array.isArray(c.against.texts) ? 'resembles' : null;
  if (node.instrument === 'cross_encoder') {
    if (typeof c.check === 'string') return c.check === 'intent' || c.check === 'statement' ? 'intent' : null;
    if (!Array.isArray(c.pair) || c.pair.length !== 2) return null;
    return isObj(c.pair[1]) && typeof c.pair[1].text === 'string' ? 'intent' : 'grounded';   // an older Says node
  }
  return null;
}

/** An Intent node's text and intent, in one shape — older Says and 'statement' nodes included. */
export function statementOf(node) {
  const c = node?.config || {};
  if (c.check === 'intent') return { text: c.text, intent: String(c.intent || '') };
  if (c.check === 'statement') return { text: c.text, intent: String(c.statement || '') };
  if (Array.isArray(c.pair)) return { text: c.pair[0], intent: String((isObj(c.pair[1]) && c.pair[1].text) || '') };
  return { text: 'run.exchange', intent: '' };
}

/** An Intent node after an edit, always in the check shape (older shapes are rewritten). */
export function withStatement(node, patch) {
  const cur = statementOf(node);
  const { pair, statement, ...rest } = node?.config || {};
  return { config: { ...rest, check: 'intent', text: cur.text, intent: cur.intent, ...patch } };
}

/** What an Intent can be about, per level: the whole exchange first. */
export function statementSources(target) {
  return {
    run: [{ value: 'run.exchange', label: 'Input and output' }, { value: 'run.input', label: 'Run input' }, { value: 'run.output', label: 'Run output' }],
    span: [{ value: 'span.exchange', label: 'Input and output' }, { value: 'span.input', label: 'Span input' }, { value: 'span.output', label: 'Span output' }],
    conversation: [
      { value: 'conversation.transcript', label: 'Whole transcript' }, { value: 'conversation.last_user', label: 'Last user message' },
      { value: 'conversation.last_assistant', label: 'Last reply' },
    ],
  }[levelOf(target)];
}

/** How a reading shows in the editor, and what is missing for it on this text (null when nothing is). */
export function describeReading(reading, text, target) {
  if (!reading || !reading.kind) return null;
  if (reading.kind === 'answered') {
    const ok = /^(run|span)\.exchange$/.test(text) || ['conversation.transcript', 'conversation.last_assistant'].includes(text);
    return {
      label: reading.negated ? 'whether the question went unanswered' : 'whether the question was answered',
      problem: ok ? null : (levelOf(target) === 'conversation' ? 'Needs the question too: pick the whole transcript or the last reply.' : 'Needs the question too: pick Input and output.'),
    };
  }
  if (reading.kind === 'repeated') {
    return {
      label: reading.negated ? 'whether every request was new' : 'whether the user asked the same thing twice',
      problem: text === 'conversation.transcript' ? null
        : (levelOf(target) === 'conversation' ? 'Read across the turns: pick the whole transcript.' : 'Read across a conversation\'s turns: use a conversation eval.'),
    };
  }
  return { label: 'a statement about the text', problem: null };
}

const SPANS_OF = { retrieval: { spans: { kind: 'tool' }, side: 'both', last: 20 } };
const FIRST = { run: 'run.output', span: 'span.output', conversation: 'conversation.transcript' };
const BOOL = { ok: { type: 'boolean' } };

/** A fresh check for an eval at `target`: instrument, config, output. */
export function defaultsFor(check, target = 'run') {
  const lvl = levelOf(target);
  if (check === 'resembles') {
    return {
      instrument: 'bi_encoder',
      config: { op: 'similarity', vector_of: FIRST[lvl], against: { texts: [] }, aggregate: 'max', threshold: DEFAULT_THRESHOLD.resembles },
      output: BOOL,
    };
  }
  if (check === 'intent') {
    return { instrument: 'cross_encoder', config: { check: 'intent', text: statementSources(lvl)[0].value, intent: '', threshold: DEFAULT_THRESHOLD.intent }, output: BOOL };
  }
  const pair = { run: [SPANS_OF.retrieval, 'run.output'], span: ['span.input', 'span.output'], conversation: ['conversation.transcript', 'conversation.last_assistant'] }[lvl];
  return { instrument: 'cross_encoder', config: { pair, threshold: DEFAULT_THRESHOLD.grounded }, output: BOOL };
}

// ---------------------------------------------------------------------------
// Where the text comes from, per level
// ---------------------------------------------------------------------------

export const SPANS_KEY = '__spans__';
export const PATH_KEY = '__path__';

/** The dropdown value for a selector. */
export function keyOf(sel) {
  if (typeof sel === 'string') return sel;
  if (isObj(sel) && isObj(sel.spans)) return SPANS_KEY;
  return PATH_KEY;
}

/**
 * Text a check can read at this level, grouped for a dropdown. `before` is
 * `stepsGroup(graph, nodeId)`: what earlier steps passed on.
 */
export function textSources(target, before = []) {
  const lvl = ['run', 'span', 'conversation'].includes(target) ? target : 'run';
  const passedOn = (before[0]?.fields || []).filter((f) => ['text', 'list', 'json'].includes(f.type)).map((f) => ({ value: f.path, label: f.label }));
  const groups = {
    run: [
      { groupLabel: 'This run', options: [{ value: SPANS_KEY, label: 'Its spans' }, { value: 'run.input', label: 'Run input' }, { value: 'run.output', label: 'Run output' }] },
      ...(passedOn.length ? [{ groupLabel: 'From earlier steps', options: passedOn }] : []),
    ],
    span: [
      { groupLabel: 'This span', options: [{ value: 'span.input', label: 'Span input' }, { value: 'span.output', label: 'Span output' }] },
      { groupLabel: 'Its run', options: [{ value: 'run.input', label: 'Run input' }, { value: 'run.output', label: 'Run output' }] },
      ...(passedOn.length ? [{ groupLabel: 'From earlier steps', options: passedOn }] : []),
    ],
    conversation: [
      { groupLabel: 'This conversation', options: [{ value: 'conversation.transcript', label: 'Whole transcript' }, { value: 'conversation.last_user', label: 'Last user message' }] },
      ...(passedOn.length ? [{ groupLabel: 'From earlier steps', options: passedOn }] : []),
    ],
  }[lvl];
  return [...groups, { groupLabel: 'Other', options: [{ value: PATH_KEY, label: 'A field by path…' }] }];
}

/**
 * What a check can JUDGE at this level: one text of the eval's own level.
 * Never a list of spans — a run's verdict is about the run.
 */
export function judgedSources(target) {
  const lvl = ['run', 'span', 'conversation'].includes(target) ? target : 'run';
  return {
    run: [{ value: 'run.output', label: 'Run output' }, { value: 'run.input', label: 'Run input' }],
    span: [{ value: 'span.output', label: 'Span output' }, { value: 'span.input', label: 'Span input' }],
    conversation: [
      { value: 'conversation.last_assistant', label: 'Last reply' }, { value: 'conversation.last_user', label: 'Last user message' },
      { value: 'conversation.transcript', label: 'Whole transcript' },
    ],
  }[lvl];
}

/** What has a stored embedding at this level — the only things Resembles can read. */
export function vectorSources(target) {
  const lvl = ['run', 'span', 'conversation'].includes(target) ? target : 'run';
  return {
    run: [{ value: 'run.output', label: 'Run output' }, { value: 'run.input', label: 'Run input' }],
    span: [{ value: 'span.output', label: 'Span output' }, { value: 'span.input', label: 'Span input' }],
    conversation: [{ value: 'conversation.transcript', label: 'Whole transcript' }],
  }[lvl];
}

/** The selector a dropdown choice means, keeping what the old one had where it applies. */
export function selectorFor(key, prev) {
  if (key === SPANS_KEY) return isObj(prev) && isObj(prev.spans) ? prev : { spans: { kind: 'tool' }, side: 'both', last: 20 };
  if (key === PATH_KEY) return typeof prev === 'string' && !/^(run|span|conversation)\.(input|output|transcript|last_user|last_assistant)$/.test(prev) ? prev : 'run.metadata.';
  return key;
}
